import { Grid, Paper, Typography } from "@mui/material";
import axios from "axios";
import React, { useEffect, useMemo, useState } from "react";
import { baseURL } from "../../api";
import { Global_Data } from "../../globalData/GlobalData";
import VARNUMBER from "../VARNUMBER/VARNUMBER";
import Vartext from "../VARTEXT/Vartext";
import VarImage from "../VARIMAGE/VarImage";
import { Icon } from "../../utils/MuiIcons/Icon";
import ComponentLoadingBoundary from "../Loading/ComponentLoadingBoundary";
import usePendingRequests from "../Loading/usePendingRequests";

function VARGROUP({ data, varValue, format, renderComponent, overrideGroupData }) {
  const { token, textValue } = Global_Data();
  const [fetchedData, setFetchedData] = useState(null);
  const { loading, trackRequest } = usePendingRequests();

  const sAction = data?.data?.sAction;
  const sDataSource = data?.data?.sDataSource;

  function replacePlaceholders(uri, values) {
    return uri?.replace(/{([^}]+)}/g, (match, key) => {
      const value = values?.[key];
      return value !== "" && value !== undefined && value !== null ? value : match;
    });
  }

  function getByPath(obj, path) {
    if (!path || obj == null) return '';
    // Normalise array notation: watchlists[0].x → watchlists.0.x
    const normalized = path.replace(/\[(\d+)\]/g, '.$1');
    return normalized.split('.').reduce((acc, key) => (acc == null ? '' : acc[key]), obj) ?? '';
  }

  async function fetchData() {
    if (sAction === 'GROUPDATA') return;
    const processedURL = replacePlaceholders(sDataSource, textValue);
    if (processedURL && !processedURL.includes("{") && !processedURL.includes("[")) {
      try {
        const response = await trackRequest(() =>
          axios.get(`${baseURL}${processedURL}`, {
            headers: { Authorization: `Bearer ${token}` }
          })
        );
        setFetchedData(response.data);
      } catch (error) {
        console.log(error);
      }
    }
  }

  // Priority: overrideGroupData (per-event from queue) > textValue listener > API fetch
  const groupData = useMemo(() => {
    if (overrideGroupData !== undefined) return overrideGroupData;
    if (sAction === 'GROUPDATA') return textValue?.[sDataSource] ?? null;
    return fetchedData;
  }, [overrideGroupData, sAction, sDataSource, textValue, fetchedData]);

  const dependencyFields = useMemo(() => {
    return data?.data?.sDataAware?.match(/{(.*?)}/g)?.map(f => f.replace(/[{}]/g, "")) || [];
  }, [data?.data?.sDataAware]);

  const dependencies = useMemo(() => {
    return dependencyFields.map(field => textValue?.[field] ?? null);
  }, [textValue, dependencyFields]);

  useEffect(() => {
    fetchData();
  }, dependencies);

  // Recursively extract sValueField values from all descendants so nested
  // VARTEXT / VARNUMBER inside BOX containers get the correct values.
  function extractAllValues(children, source, result = {}) {
    children?.forEach(child => {
      if (child?.data?.sAction === "GROUPDATA" && child?.data?.sValueField && child?.component?.sName) {
        result[child.component.sName] = getByPath(source, child.data.sValueField)?.toString() ?? "";
      }
      if (child?.child?.length) {
        extractAllValues(child.child, source, result);
      }
    });
    return result;
  }

  const mergedVarValue = useMemo(() => {
    if (!groupData) return varValue || {};
    const childValues = extractAllValues(data?.child, groupData);
    return { ...(varValue || {}), ...childValues };
  }, [groupData, varValue, data?.child]);

  function renderChild(child, index) {
    const sType = child?.component?.sType;

    if (sType === "VARNUMBER") {
      const childData = {
        ...child,
        data: { ...child.data, sDataSource: "", sDataAware: "", sAction: "DEFAULT" }
      };
      return (
        <VARNUMBER
          key={child?.component?.sName ?? index}
          data={childData}
          varValue={mergedVarValue}
          format={format}
        />
      );
    }

    if (sType === "VARTEXT") {
      const childData = {
        ...child,
        data: { ...child.data, sDataSource: "", sDataAware: "", sAction: "DEFAULT" }
      };
      return (
        <Vartext
          key={child?.component?.sName ?? index}
          data={childData}
          varValue={mergedVarValue}
          format={format}
        />
      );
    }

    if (sType === "VARIMAGE") {
      return (
        <VarImage
          key={child?.component?.sName ?? index}
          data={child}
          varValue={mergedVarValue}
          listenerData={groupData}
        />
      );
    }

    if (sType === "TYPOGRAPHY") {
      return (
        <Grid item key={child?.component?.sName ?? index} {...child?.grid_props}>
          <Typography {...child?.component?.sProps} id={child?.component?.sName}>
            {child?.component?.sLabel}
          </Typography>
        </Grid>
      );
    }

    if (sType === "ICON") {
      const iconName = child?.component?.sIcon?.replace("Icon", "");
      return (
        <Grid item key={child?.component?.sName ?? index} {...child?.grid_props}>
          <Icon id={child?.component?.sName} {...child?.component?.sProps} iconName={iconName} />
        </Grid>
      );
    }

    // BOX: render as a Grid container and recurse into its children
    if (sType === "BOX") {
      return (
        <Grid item key={child?.component?.sName ?? index} {...child?.grid_props} sx={{ minWidth: 0, ...(child?.grid_props?.sx) }}>
          <Grid container id={child?.component?.sName} {...child?.component?.sProps}>
            {child?.child?.map((nested, i) => renderChild(nested, i))}
          </Grid>
        </Grid>
      );
    }

    // Fallback: delegate to Form.js renderComponent if provided
    if (renderComponent && child) {
      return renderComponent([child], mergedVarValue);
    }

    return null;
  }

  const { elevation, sx, ...restProps } = data?.component?.sProps || {};

  return (
    <Grid item {...data?.grid_props}>
      <ComponentLoadingBoundary
        loading={loading}
        label={`Loading ${data?.component?.sName}`}
        minHeight={64}
        size={24}
      >
        <Paper
          elevation={elevation ?? 0}
          sx={sx}
          {...restProps}
          id={data?.component?.sName}
        >
          <Grid container>
            {data?.child?.map((child, index) => renderChild(child, index))}
          </Grid>
        </Paper>
      </ComponentLoadingBoundary>
    </Grid>
  );
}

export default VARGROUP;
